import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  FileService,
  FileRecord,
  FileListParams,
  UploadUrlOptions,
  StorageUsage,
} from '@/services/api/fileService';
import * as errorUtils from '@/utils/errors';

// ============================================================================
// Types
// ============================================================================

export interface UploadProgress {
  id: string;
  filename: string;
  progress: number;
  status: 'pending' | 'uploading' | 'confirming' | 'complete' | 'error';
  error?: string;
  fileId?: string;
}

interface FileState {
  files: FileRecord[];
  selectedFileId: string | null;
  uploads: UploadProgress[];
  usage: StorageUsage | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };

  // Actions
  fetchFiles: (orgSlug: string, params?: FileListParams) => Promise<void>;
  requestUploadUrl: (orgSlug: string, options: UploadUrlOptions) => Promise<{
    fileId: string;
    uploadUrl: string;
    key: string;
    headers?: Record<string, string>;
  }>;
  confirmUpload: (orgSlug: string, fileId: string, etag?: string) => Promise<FileRecord | undefined>;
  deleteFile: (orgSlug: string, fileId: string) => Promise<void>;
  updateFile: (orgSlug: string, fileId: string, data: { description?: string; tags?: string[] }) => Promise<void>;
  updateVisibility: (orgSlug: string, fileId: string, visibility: 'private' | 'project' | 'org' | 'public') => Promise<void>;
  getDownloadUrl: (orgSlug: string, fileId: string, passphrase?: string) => Promise<string | undefined>;
  fetchUsage: (orgSlug: string) => Promise<void>;
  setSelectedFile: (fileId: string | null) => void;
  addUpload: (upload: UploadProgress) => void;
  updateUpload: (id: string, updates: Partial<UploadProgress>) => void;
  removeUpload: (id: string) => void;
  clearError: () => void;
}

// ============================================================================
// Store
// ============================================================================

export const useFileStore = create<FileState>()(
  devtools(
    (set, get) => ({
      files: [],
      selectedFileId: null,
      uploads: [],
      usage: null,
      isLoading: false,
      error: null,
      pagination: { page: 1, limit: 20, total: 0, pages: 0 },

      fetchFiles: async (orgSlug, params) => {
        set({ isLoading: true, error: null });
        try {
          const response = await FileService.listFiles(orgSlug, params);
          set({
            files: Array.isArray(response.data) ? response.data : [],
            pagination: response.pagination
              ? {
                  page: response.pagination.page,
                  limit: response.pagination.limit,
                  total: response.pagination.total,
                  pages: response.pagination.totalPages || response.pagination.pages || 0,
                }
              : { page: 1, limit: 20, total: 0, pages: 0 },
            isLoading: false,
          });
        } catch (error: unknown) {
          set({
            error: errorUtils.getApiErrorMessage(error, 'Failed to fetch files'),
            isLoading: false,
          });
        }
      },

      requestUploadUrl: async (orgSlug, options) => {
        set({ error: null });
        try {
          const response = await FileService.requestUploadUrl(orgSlug, options);
          const data = response.data!;
          return {
            fileId: data.fileId,
            uploadUrl: data.uploadUrl,
            key: data.key,
            headers: data.headers,
          };
        } catch (error: unknown) {
          const msg = errorUtils.getApiErrorMessage(error, 'Failed to get upload URL');
          set({ error: msg });
          throw new Error(msg);
        }
      },

      confirmUpload: async (orgSlug, fileId, etag) => {
        set({ error: null });
        try {
          const response = await FileService.confirmUpload(orgSlug, fileId, etag);
          const file = response.data;
          if (file) {
            set((state) => ({
              files: [file, ...state.files],
            }));
          }
          return file;
        } catch (error: unknown) {
          const msg = errorUtils.getApiErrorMessage(error, 'Failed to confirm upload');
          set({ error: msg });
          throw new Error(msg);
        }
      },

      deleteFile: async (orgSlug, fileId) => {
        set({ error: null });
        try {
          await FileService.deleteFile(orgSlug, fileId);
          set((state) => ({
            files: state.files.filter((f) => f._id !== fileId),
            selectedFileId: state.selectedFileId === fileId ? null : state.selectedFileId,
          }));
        } catch (error: unknown) {
          const msg = errorUtils.getApiErrorMessage(error, 'Failed to delete file');
          set({ error: msg });
          throw new Error(msg);
        }
      },

      updateFile: async (orgSlug, fileId, data) => {
        set({ error: null });
        try {
          const response = await FileService.updateFile(orgSlug, fileId, data);
          const updated = response.data;
          if (updated) {
            set((state) => ({
              files: state.files.map((f) => (f._id === fileId ? updated : f)),
            }));
          }
        } catch (error: unknown) {
          const msg = errorUtils.getApiErrorMessage(error, 'Failed to update file');
          set({ error: msg });
          throw new Error(msg);
        }
      },

      updateVisibility: async (orgSlug, fileId, visibility) => {
        set({ error: null });
        try {
          const response = await FileService.updateVisibility(orgSlug, fileId, visibility);
          const updated = response.data;
          if (updated) {
            set((state) => ({
              files: state.files.map((f) => (f._id === fileId ? updated : f)),
            }));
          }
        } catch (error: unknown) {
          const msg = errorUtils.getApiErrorMessage(error, 'Failed to update visibility');
          set({ error: msg });
          throw new Error(msg);
        }
      },

      getDownloadUrl: async (orgSlug, fileId, passphrase) => {
        set({ error: null });
        try {
          const response = await FileService.getDownloadUrl(orgSlug, fileId, passphrase);
          return response.data?.downloadUrl;
        } catch (error: unknown) {
          const msg = errorUtils.getApiErrorMessage(error, 'Failed to get download URL');
          set({ error: msg });
          throw new Error(msg);
        }
      },

      fetchUsage: async (orgSlug) => {
        try {
          const response = await FileService.getUsage(orgSlug);
          set({ usage: response.data || null });
        } catch (error: unknown) {
          console.error('Failed to fetch storage usage:', error);
        }
      },

      setSelectedFile: (fileId) => set({ selectedFileId: fileId }),

      addUpload: (upload) =>
        set((state) => ({ uploads: [...state.uploads, upload] })),

      updateUpload: (id, updates) =>
        set((state) => ({
          uploads: state.uploads.map((u) =>
            u.id === id ? { ...u, ...updates } : u
          ),
        })),

      removeUpload: (id) =>
        set((state) => ({
          uploads: state.uploads.filter((u) => u.id !== id),
        })),

      clearError: () => set({ error: null }),
    }),
    { name: 'file-store' }
  )
);
